import { datadogRum, RumEvent } from '@datadog/browser-rum';
import { init as initFullStory } from '@fullstory/browser';

import { Analytics } from '../analytics';
import { config } from '../config';
import {
  BARE_NETWORK_ERROR_MESSAGE_PATTERNS,
  CEFSHARP_BOT_ERROR_PATTERN,
  MODULE_IMPORT_ABORT_MESSAGE_PATTERNS,
  THIRD_PARTY_NOISE_MESSAGE_PATTERNS,
  THIRD_PARTY_NOISE_PATTERNS,
} from './thirdPartyNoisePatterns';
import { Logger } from './Logger';

const isThirdPartyNoise = (event: RumEvent) => {
  if (event.type !== 'error') {
    return false;
  }
  const { message, stack } = event.error;
  return (
    THIRD_PARTY_NOISE_PATTERNS.some((pattern) => message.includes(pattern) || stack?.includes(pattern)) ||
    THIRD_PARTY_NOISE_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern))
  );
};

// A "PreventDefaultPassive" intervention report means a passive scroll/touch listener (registered
// by a monitoring SDK, not app code) tried to preventDefault — a browser performance notice, not a bug.
const isPassiveListenerIntervention = (event: RumEvent) =>
  event.type === 'error' && event.error.source === 'report' && event.error.type === 'PreventDefaultPassive';

// Bare cross-origin script errors are unactionable noise; browsers sometimes wrap the placeholder as `Uncaught "Script error."`, so match substring not exact equality.
// A first-party TypeError always carries a real stack (we control the bundle/sourcemaps) — an
// entirely stack-less error is the browser's security redaction for a cross-origin script, no
// matter what its message text says, so match on the placeholder stack rather than the message.
const isBareScriptError = (event: RumEvent) =>
  event.type === 'error' &&
  (event.error.message.includes('Script error.') ||
    (event.error.message === 'undefined' && /at undefined @\s*$/.test(event.error.stack ?? '')) ||
    event.error.stack === 'No stack, consider using an instance of Error');

const isCefSharpBotError = (event: RumEvent) => event.type === 'error' && CEFSHARP_BOT_ERROR_PATTERN.test(event.error.message);

// A dynamically-imported JS chunk load aborted by page navigation (see MODULE_IMPORT_ABORT_MESSAGE_PATTERNS).
const isModuleImportAbort = (event: RumEvent) =>
  event.type === 'error' && MODULE_IMPORT_ABORT_MESSAGE_PATTERNS.some((pattern) => event.error.message.includes(pattern));

// A fetch() that never got any response at all (see BARE_NETWORK_ERROR_MESSAGE_PATTERNS).
const isBareNetworkError = (event: RumEvent) =>
  event.type === 'error' && BARE_NETWORK_ERROR_MESSAGE_PATTERNS.some((pattern) => event.error.message.includes(pattern));

export const initMonitoring = () => {
  Logger.init();

  // Initialize Segment analytics
  Analytics.init(config.analytics);

  if (config.fullStoryOrgId) {
    initFullStory({ orgId: config.fullStoryOrgId });
  }

  if (config.dataDogEnabled && !datadogRum.getInternalContext()) {
    datadogRum.init({
      applicationId: config.dataDog.appId,
      clientToken: config.dataDog.clientToken,
      site: config.dataDog.site,
      service: config.dataDog.service,
      env: config.environment,
      version: config.version,
      trackUserInteractions: true,
      trackFrustrations: true,
      defaultPrivacyLevel: 'mask-user-input',
      allowedTracingUrls: [`https://${window.location.hostname}/v1/`],
      beforeSend: (event) =>
        !isPassiveListenerIntervention(event) &&
        !isThirdPartyNoise(event) &&
        !isBareScriptError(event) &&
        !isCefSharpBotError(event) &&
        !isModuleImportAbort(event) &&
        !isBareNetworkError(event),
    });

    datadogRum.startSessionReplayRecording();
  }
};
